import { useState, useEffect } from "react";
import { Box, Button, Grid, Link, Breadcrumbs, Tooltip, Typography, TextField, TextareaAutosize } from "@mui/material";
import { axios } from "utilities";
import { FormSubsection, DisplayField, MUITableD } from "components";
import { useTranslation } from "react-i18next";

type Props = {
  record1: any;
  record2: any
};

type DexComp = {
  ident: string;
  description: string,
  rec1: string,
  rec2: string,
  delta: string
}

const DexComparison = (props: Props) => {
  const { record1, record2 } = props;
  const { t } = useTranslation("telemetry"); // Get translation
  const [dex1Data, setDex1Data] = useState("");
  const [dex2Data, setDex2Data] = useState("");
  //const [compData, setCompData] = useState<DexComp>();

  const r1 = record1 ? record1 : {};
  const r2 = record1 ? record2 : {};

  const handleRowClick = () => { };

  useEffect(() => {
    if (r1.s3Key && r2.s3Key) {
      axios({
        method: "post",
        data: { key1: r1.s3Key },
        url: "/api/vvr/monitor/file",
      }).then((res) => {
        setDex1Data(res.data);
        axios({
          method: "post",
          data: { key1: r2.s3Key },
          url: "/api/vvr/monitor/file",
        }).then((res) => {
          setDex2Data(res.data);
        }).catch((reason) => {
          console.log(reason);
        });
      }).catch((reason) => {
        console.log(reason);
      });
    }
  }, [r1, r2]);

  const parseDex = (dexfile: string): Map<string, string> => {
    let dexObj = new Map<string, string>();
    const dexdata = dexfile.replace('\r', '');
    const lines = dexdata.split("\n");
    for (var i = 0; i < lines.length; i++) {
      const line = lines[i];
      const items = line.split("*");
      if (items.length > 1) {
        const ri = items[0].toUpperCase();
        for (var j = 1; j < items.length; j++) {
          if (items[j].length > 0) {
            const ident = ri + String(j).padStart(2, "0");
            let val = dexObj.get(ident) || ""
            let item = items[j].replace('\r', '').replace('\n', '').trim();
            if (val === "") {
              dexObj.set(ident, item);
            } else {
              dexObj.set(ident, val + "," + item);
            }
          }
        }
      }
    }
    return dexObj;
  };

  const compareFiles = (): Array<DexComp> => {
    let ret = new Array<DexComp>();
    if (dex1Data && dex2Data) {

      var start = dex1Data.indexOf("DXS*");
      var end = dex1Data.indexOf("DXE*") + 4;
      var dex1 = dex1Data.substring(start, end);
      dex1 = dex1.replaceAll("&#x0D;", "\r").replaceAll("&#x0A;", "\n")
      let dexObj1 = parseDex(dex1);

      var start = dex2Data.indexOf("DXS*");
      var end = dex2Data.indexOf("DXE*") + 4;
      var dex2 = dex2Data.substring(start, end);
      dex2 = dex2.replaceAll("&#x0D;", "\r").replaceAll("&#x0A;", "\n")
      let dexObj2 = parseDex(dex2);

      var rec1 = dexObj1.get("VA101") || "0";
      var rec2 = dexObj2.get("VA101") || "0";
      var delta = rec1 != rec2 ? (parseInt(rec1) - parseInt(rec2)).toFixed(0) : "";
      ret.push({ ident: "VA101", description: "Value of all paid vend sales", rec1: rec1, rec2: rec2, delta: delta });

      rec1 = dexObj1.get("CA305") || "0";
      rec2 = dexObj2.get("CA305") || "0";
      delta = rec1 != rec2 ? (parseInt(rec1) - parseInt(rec2)).toFixed(0) : "";
      ret.push({ ident: "CA305", description: "Value of cash in", rec1: rec1, rec2: rec2, delta: delta });

      rec1 = dexObj1.get("DA201") || "0";
      rec2 = dexObj2.get("DA201") || "0";
      delta = rec1 != rec2 ? (parseInt(rec1) - parseInt(rec2)).toFixed(0) : "";
      ret.push({ ident: "DA201", description: "Value of cashless sales", rec1: rec1, rec2: rec2, delta: delta });
    }
    return ret;
  }

  /*
  const handleLoadFile = (key: String) => {
    axios({
      method: "post",
      data: { key1: key },
      url: "/api/vvr/monitor/file",
    }).then((res) => {
      setPayload(res.data)
    }).catch((reason) => {
      console.log(reason);
    });
  }
  */

  var compData = new Array<DexComp>();

  if (dex1Data && dex2Data) {
    compData = compareFiles();
  }

  const columns = [
    { name: "ident", label: t("DEX Identifier") },
    { name: "description", label: t("Description") },
    { name: "rec1", label: t("1st Record") },
    { name: "rec2", label: t("2nd Record") },
    { name: "delta", label: t("Delta") },
  ];

  const modalStyle = {
    position: 'absolute',
    overflow: 'scroll',
    height: '100%',
    display: 'block',
    left: '10%',
    right: '10%',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  return (
    <>

      {/* Fixed Header */}
      <Box sx={modalStyle}>
        <FormSubsection title={t("DEX Data")}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <DisplayField
                data={r1.dexMetadata}
                name="readDateTime"
                label={t("Record 1 Timestamp")}
              />
            </Grid>
            <Grid item xs={2}>
              <DisplayField
                data={r1.dexMetadata}
                name="gmtOffset"
                label={t("Record 1 UTC Offset")}
              />
            </Grid>
            <Grid item xs={2}>
              <DisplayField
                data={r1.dexMetadata}
                name="dexReason"
                label={t("Record 1 DEX Reason")}
              />
            </Grid>
            <Grid item xs={2}>
              <DisplayField
                data={r1.dexMetadata}
                name="fileSize"
                label={t("Record 1 File Size")}
              />
            </Grid>
            <Grid item xs={6}>
              <DisplayField
                data={r2.dexMetadata}
                name="readDateTime"
                label={t("Record 2 Timestamp")}
              />
            </Grid>
            <Grid item xs={2}>
              <DisplayField
                data={r2.dexMetadata}
                name="gmtOffset"
                label={t("Record 2 UTC Offset")}
              />
            </Grid>
            <Grid item xs={2}>
              <DisplayField
                data={r2.dexMetadata}
                name="dexReason"
                label={t("Record 2 DEX Reason")}
              />
            </Grid>
            <Grid item xs={2}>
              <DisplayField
                data={r2.dexMetadata}
                name="fileSize"
                label={t("Record 2 File Size")}
              />
            </Grid>
          </Grid>
        </FormSubsection>

        <FormSubsection title={t("DEX Comparison")}>
          <MUITableD
            title=""
            data={compData}
            cols={columns}
            sortOrder={{ name: "ident", direction: "asc" }}
            onRowClick={handleRowClick}
          />
        </FormSubsection>
      </Box>

    </>
  );
};

export default DexComparison;
